/**
 * Sector persistence (BUILDLIST 56.6).
 *
 * The scarred planets are the only part of a sector that is not plain data:
 * fifteen thousand floats each. Their serialisable form is a **scar log** —
 * every vertex a battle left below (or above) the generated terrain, plus the
 * scorch tint, the mass and whether the planet still stands — exact by
 * construction and a few kilobytes per crater. (DIRECTION §9.7 pictured an
 * impact log replayed through the crater code; the match keeps no such log,
 * and a replay would have to match the WASM and JS crater paths bit for bit.
 * The scar log sidesteps both; impacts can be logged later for display.)
 *
 * A save pins the `BATTLE_VERSION` it was played under: stored outcomes are
 * the truth, a replay is for watching, and a sector saved under another
 * version loads with a warning rather than re-resolving its history.
 *
 * `replaySector` rebuilds a sector from its seed, options, order log and
 * stored outcomes — the sector's own replay, independent of the bots.
 *
 * **Terrain must match (R6).** A scar log is vertex indices into a freshly generated
 * planet. If the generator changed since the save (detail per radius, a mapgen change),
 * the indices land on different ground: spikes, seams, crater floors as cliffs — silently.
 * So every log carries its planet's vertex count and a fingerprint of the generated terrain,
 * and loading refuses with a clear message when either differs. Saves from before R6 carry
 * no fingerprint; they load, flagged `unverifiedTerrain`. Migrating a mismatched save is R13.
 */

import type { PlanetState } from '../types';
import { BATTLE_VERSION } from '../match/match';
import { DEFAULT_RULES, createSector, advanceSector, applyBattleOutcome, issueOrder, type SectorOptions } from './sector';
import { planetsForSystem } from './battle';
import { rebuildPlanets } from '../core/terrainEdits';
import type { BattleOutcome, BattleRecord, RunningBattle, SectorState, SectorSystem } from './types';

/**
 * The save format's version. 1: the first format. 2 (R6): scar logs carry a terrain fingerprint.
 * 3 (R13): systems carry `destroyedProps`; battle outcomes carry `terrainEdits` and `chronicle`.
 * An older save is **migrated** step by step (`MIGRATIONS`); a newer one is refused with its version named.
 */
export const SECTOR_SAVE_VERSION = 4;
/** The oldest save this build still reads. */
export const OLDEST_SAVE_VERSION = 1;

/**
 * R13: one step per version, applied in order from the save's version up to this build's. A step edits
 * the raw save as data and must be total (every save of its version migrates). Add one when the format
 * changes and bump SECTOR_SAVE_VERSION.
 */
const MIGRATIONS: Record<number, (save: SectorSave) => SectorSave> = {
  // 1 → 2: scar logs gain a fingerprint; a v1 log has none, so the loader marks the save unverified (R6)
  1: save => ({ ...save, saveVersion: 2 }),
  // 2 → 3: systems gain destroyedProps (nothing destroyed before was recorded: empty); outcomes' new fields are optional
  2: save => ({ ...save, saveVersion: 3, state: { ...save.state, systems: save.state.systems.map(s => ({ ...s, destroyedProps: s.destroyedProps ?? { rocks: [], flora: [], hazards: [] } })) } }),
  // 3 → 4 (62.3): rules gain the drone dials and squads a drone count — every squad starts with the free count
  3: save => {
    const rules = { ...save.state.rules, dronesPerSquad: save.state.rules.dronesPerSquad ?? DEFAULT_RULES.dronesPerSquad, droneCost: save.state.rules.droneCost ?? DEFAULT_RULES.droneCost };
    return { ...save, saveVersion: 4, state: { ...save.state, rules, squads: save.state.squads.map(q => ({ ...q, drones: q.drones ?? rules.dronesPerSquad })) } };
  },
};

/** Bring a save up to this build's format. Returns the migrated save and the version it came from. */
export function migrateSave(save: SectorSave): { save: SectorSave; from: number } {
  const from = save.saveVersion;
  if (from > SECTOR_SAVE_VERSION) throw new Error(`this sector was saved by a newer build (save version ${from}; this build reads up to ${SECTOR_SAVE_VERSION})`);
  if (from < OLDEST_SAVE_VERSION) throw new Error(`sector save version ${from} is older than this build reads (${OLDEST_SAVE_VERSION}–${SECTOR_SAVE_VERSION})`);
  let s = save;
  while (s.saveVersion < SECTOR_SAVE_VERSION) {
    const step = MIGRATIONS[s.saveVersion];
    if (!step) throw new Error(`no migration from sector save version ${s.saveVersion}`);
    s = step(s);
  }
  return { save: s, from };
}

import { terrainFingerprint } from './terrainPrint';
import { TERRAIN_GENERATOR_VERSION } from '../constants/world';
export { terrainFingerprint } from './terrainPrint';

/** A saved scar log does not fit this build's terrain (R6). */
export class TerrainMismatchError extends Error {
  constructor(message: string) { super(message); this.name = 'TerrainMismatchError'; }
}

/** The vertices a battle changed, against the generated terrain. */
export interface PlanetScarLog {
  index: number;
  /** R6: the generated planet's vertex count and terrain fingerprint (absent in saves from before R6). */
  n?: number;
  fp?: string;
  /** R13: the terrain generator's version that made the planet (absent before it existed), so a mismatch is named. */
  gen?: number;
  /** Changed heightmap vertices: [i0, v0, i1, v1, …]. */
  h: number[];
  /** Non-zero scorch weights: [i0, w0, …]. */
  s: number[];
  mass: number;
  alive: boolean;
}

export function planetScarLog(p: PlanetState, index: number): PlanetScarLog | null {
  const base = p.initialHeightmap;
  const h: number[] = [];
  if (base) {
    for (let i = 0; i < p.heightmap.length; i++) if (p.heightmap[i] !== base[i]) h.push(i, p.heightmap[i]);
  }
  const s: number[] = [];
  if (p.scorch) for (let i = 0; i < p.scorch.length; i++) if (p.scorch[i] !== 0) s.push(i, p.scorch[i]);
  if (!h.length && !s.length && p.mass === p.initialMass && p.alive) return null;
  const gen = base ?? p.heightmap;
  return { index, n: gen.length, fp: terrainFingerprint(gen), gen: TERRAIN_GENERATOR_VERSION, h, s, mass: p.mass, alive: p.alive };
}

/**
 * The log applied to a freshly generated planet gives back the scarred one. Throws
 * TerrainMismatchError when the log was made on different terrain (R6); `where` names
 * the planet in the message.
 */
export function applyScarLog(p: PlanetState, log: PlanetScarLog, where = `planet ${log.index}`): PlanetState {
  const gen = p.initialHeightmap ?? p.heightmap;
  if (log.gen !== undefined && log.gen !== TERRAIN_GENERATOR_VERSION) {
    throw new TerrainMismatchError(`${where}: the save's terrain was generated by generator version ${log.gen}; this build's is ${TERRAIN_GENERATOR_VERSION} (now ${TERRAIN_GENERATOR_VERSION}). The terrain generator changed since this sector was saved, so its scars cannot be placed on the new terrain.`);
  }
  if (log.n !== undefined && log.n !== gen.length) {
    throw new TerrainMismatchError(`${where}: the save's terrain has ${log.n} vertices, this build generates ${gen.length}. The terrain generator changed since this sector was saved, so its scars cannot be placed.`);
  }
  if (log.fp !== undefined && log.fp !== terrainFingerprint(gen)) {
    throw new TerrainMismatchError(`${where}: the save's terrain (fingerprint ${log.fp}) is not what this build generates (${terrainFingerprint(gen)}). The terrain generator changed since this sector was saved, so its scars cannot be placed.`);
  }
  const heightmap = new Float32Array(gen);
  for (let k = 0; k < log.h.length; k += 2) heightmap[log.h[k]] = log.h[k + 1];
  let scorch: Float32Array | undefined;
  if (log.s.length) {
    scorch = new Float32Array(p.heightmap.length);
    for (let k = 0; k < log.s.length; k += 2) scorch[log.s[k]] = log.s[k + 1];
  }
  return { ...p, heightmap, scorch, mass: log.mass, alive: log.alive };
}

export function scarLogsOf(planets: PlanetState[]): PlanetScarLog[] {
  return planets.map((p, i) => planetScarLog(p, i)).filter((x): x is PlanetScarLog => x !== null);
}

// -----------------------------------------------------------------------------
// Save / load
// -----------------------------------------------------------------------------

type SavedSystem = Omit<SectorSystem, 'planets'> & { planetScars?: PlanetScarLog[] };
/** A battle under way, saved: its setup without planets, its outcome's planets as scar logs. */
type SavedRunning = Omit<RunningBattle, 'setup' | 'outcome'> & {
  setup: Omit<RunningBattle['setup'], 'planets'>;
  outcome: Omit<BattleOutcome, 'planets'>;
  planetScars: PlanetScarLog[];
};

export interface SectorSave {
  saveVersion: number;
  /** What the battles were resolved under (Match.BATTLE_VERSION). */
  battleVersion: number;
  savedDay: number;
  state: Omit<SectorState, 'systems' | 'pending' | 'running'> & { systems: SavedSystem[]; pending: (Omit<NonNullable<SectorState['pending']>, 'planets'>) | null; running?: SavedRunning[] };
}

/** The whole sector as data: planets as scar logs, the pending battle without its planets. */
export function serializeSector(state: SectorState): SectorSave {
  const systems: SavedSystem[] = state.systems.map(sys => {
    const { planets, ...rest } = sys;
    const planetScars = planets ? scarLogsOf(planets) : undefined;
    return planets ? { ...rest, planetScars } : rest;
  });
  const pending = state.pending ? (({ planets: _p, ...rest }) => rest)(state.pending) : null;
  const running: SavedRunning[] | undefined = state.running?.length
    ? state.running.map(r => {
      const { planets: _sp, ...setup } = r.setup;
      const { planets, ...outcome } = r.outcome;
      return { ...r, setup, outcome, planetScars: scarLogsOf(planets) };
    })
    : undefined;
  const { running: _r, ...rest } = state;
  return { saveVersion: SECTOR_SAVE_VERSION, battleVersion: BATTLE_VERSION, savedDay: state.day, state: { ...rest, systems, pending, ...(running ? { running } : {}) } };
}

export function sectorToJson(state: SectorState): string {
  return JSON.stringify(serializeSector(state));
}

export interface LoadedSector {
  state: SectorState;
  /** The save's battle version differs from this build's: its stored outcomes stand, but new battles may resolve differently. */
  staleBattleVersion: boolean;
  /** R6: the save predates terrain fingerprints, so its scars were placed without a check. */
  unverifiedTerrain: boolean;
  /** R13: the save was migrated from this older format version (absent when it was current). */
  migratedFrom?: number;
  /**
   * R13: these systems' scar logs did not fit this build's terrain (the mesh changed), so their planets
   * were rebuilt from their battles' terrain edit logs instead — the same craters at the same places.
   */
  rebuiltFromEdits?: string[];
}

/**
 * Rebuild the sector: the save migrated to this format, planets regenerated from each system's map seed
 * and the scar logs applied. Where the scar logs no longer fit this build's terrain (R6) but the
 * system's battles all carry terrain edit logs, the planets are rebuilt from those instead (R13's gate:
 * a save loads at another detail level with its craters in place); otherwise the mismatch refuses.
 */
export function deserializeSector(saveIn: SectorSave): LoadedSector {
  const { save, from } = migrateSave(saveIn);
  let unverifiedTerrain = false;
  const rebuiltFromEdits: string[] = [];
  const systems: SectorSystem[] = save.state.systems.map(sys => {
    const { planetScars, ...rest } = sys;
    if (!planetScars) return rest as SectorSystem;
    const fresh = planetsForSystem(rest as SectorSystem);
    const fallback = (): SectorSystem | null => {
      const battles = save.state.battles.filter(b => b.setup.systemId === rest.id);
      if (!battles.length || !battles.every(b => b.outcome.terrainEdits)) return null;
      rebuiltFromEdits.push(rest.id);
      return { ...(rest as SectorSystem), planets: rebuildPlanets(fresh, battles.flatMap(b => b.outcome.terrainEdits!)) };
    };
    for (const l of planetScars) {
      if (l.index >= fresh.length) { const r = fallback(); if (r) return r; throw new TerrainMismatchError(`${rest.name ?? rest.id}: the save has scars on planet ${l.index + 1}, this build generates ${fresh.length} planets there. The map generator changed since this sector was saved.`); }
      if (l.fp === undefined) unverifiedTerrain = true;
    }
    try {
      const planets = fresh.map((p, i) => { const log = planetScars.find(l => l.index === i); return log ? applyScarLog(p, log, `${rest.name ?? rest.id}, planet ${i + 1}`) : p; });
      return { ...(rest as SectorSystem), planets };
    } catch (err) {
      if (!(err instanceof TerrainMismatchError)) throw err;
      const r = fallback();
      if (r) return r;
      throw err;
    }
  });
  const { running: savedRunning, ...savedRest } = save.state;
  let state: SectorState = { ...savedRest, systems, pending: null };
  if (savedRunning?.length) {
    // a running battle's outcome planets: regenerated and scarred, checked like the systems' (R6)
    const running: RunningBattle[] = savedRunning.map(r => {
      const sys = systems.find(x => x.id === r.setup.systemId)!;
      const fresh = planetsForSystem(sys);
      for (const l of r.planetScars) if (l.index >= fresh.length) throw new TerrainMismatchError(`${sys.name ?? sys.id}: a running battle has scars on planet ${l.index + 1}, this build generates ${fresh.length} planets there.`);
      const planets = fresh.map((p, i) => { const log = r.planetScars.find(l => l.index === i); return log ? applyScarLog(p, log, `${sys.name ?? sys.id}, planet ${i + 1} (battle under way)`) : p; });
      const { planetScars: _ps, ...rest } = r;
      return { ...rest, setup: { ...r.setup, planets: sys.planets } as RunningBattle['setup'], outcome: { ...r.outcome, planets } };
    });
    state = { ...state, running };
  }
  if (save.state.pending) {
    const sys = systems.find(x => x.id === save.state.pending!.systemId);
    state = { ...state, pending: { ...save.state.pending, planets: sys?.planets } };
  }
  return {
    state, staleBattleVersion: save.battleVersion !== BATTLE_VERSION, unverifiedTerrain,
    ...(from !== SECTOR_SAVE_VERSION ? { migratedFrom: from } : {}),
    ...(rebuiltFromEdits.length ? { rebuiltFromEdits } : {}),
  };
}

/**
 * R13 prototype (decision 38): a system's scarred planets rebuilt from fresh generation plus every
 * battle's terrain edits, in order — the durable record a change of mesh can re-apply. Bit-for-bit
 * equal to the scar-log planets on the JS crater path (tests/terrainEdits.test.ts).
 */
export function rebuildSystemFromEdits(sys: SectorSystem, battles: readonly BattleRecord[]): PlanetState[] {
  const edits = battles.filter(b => b.setup.systemId === sys.id).flatMap(b => b.outcome.terrainEdits ?? []);
  return rebuildPlanets(planetsForSystem(sys), edits);
}

export function sectorFromJson(json: string): LoadedSector {
  return deserializeSector(JSON.parse(json) as SectorSave);
}

// -----------------------------------------------------------------------------
// Replay: seed + options + orders + stored outcomes
// -----------------------------------------------------------------------------

/**
 * Rebuild the sector from its seed, options and order log, applying each
 * stored outcome to the battle it belongs to (planets restored from the
 * outcome's scar log over the battle's starting planets). Deterministic by the
 * same rules the live sector obeys; stops where the record stops.
 */
export function replaySector(seed: string, options: SectorOptions, orderLog: SectorState['orderLog'], battles: BattleRecord[], untilDay: number): SectorState {
  let s = createSector(seed, options);
  const outcomes = new Map(battles.map(b => [b.outcome.battleId, b]));
  const resolve = (st: SectorState): SectorState => {
    let x = st;
    while (x.pending) {
      const rec = outcomes.get(x.pending.id);
      if (!rec) return x; // the record ends here
      const start = x.pending.planets ?? planetsForSystem(x.systems.find(y => y.id === x.pending!.systemId)!);
      const planets = start.map((p, i) => { const log = rec.planetScars?.find(l => l.index === i); return log ? applyScarLog(p, log) : p; });
      const outcome: BattleOutcome = { ...rec.outcome, planets };
      x = applyBattleOutcome(x, outcome);
    }
    return x;
  };
  // run the clock to a day, resolving every recorded battle on the way (the clock stops at each)
  const advanceTo = (st: SectorState, day: number): SectorState => {
    let x = st;
    let guard = 0;
    while (x.day < day - 1e-9 && guard++ < 10_000) {
      x = advanceSector(x, day - x.day);
      if (x.pending) { x = resolve(x); if (x.pending) return x; }
      if (x.victor) return x;
    }
    return x;
  };
  for (const entry of orderLog) {
    if (entry.day > untilDay + 1e-9) break;
    s = advanceTo(s, entry.day);
    if (s.pending) return s;
    try { s = issueOrder(s, entry.squadId, entry.order); } catch { /* an order the replayed state refuses was refused live too */ }
  }
  s = advanceTo(s, untilDay);
  // the turn counter is not part of the clock; the live sector counts turns the host ran
  return s;
}
